import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Calendar as CalendarIcon, Plus, Trash2, Clock, Radio, Upload, X, FileText, ChevronDown } from 'lucide-react';
import { Card, CardContent } from '../components/ui/Card';
import { Badge, type BadgeVariant } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Dialog } from '../components/ui/Dialog';
import { Input, Textarea, Label } from '../components/ui/Input';
import { PageLoader, Spinner } from '../components/ui/Spinner';
import { EmptyState } from '../components/ui/EmptyState';
import { apiFetch, apiUpload, ApiError } from '../lib/apiFetch';
import { formatDateTime, intlLocale } from '../lib/format';
import { useToast } from '../components/ui/Toast';
import { useT, type TFunction } from '../i18n';
import type { TKey } from '../i18n/ru';

interface Material {
  id: number;
  course_id: number;
  course_title: string;
  type_code: string;
  title: string | null;
  scheduled_at: string;
  published_at: string | null;
  is_cancelled: boolean;
  last_error: string | null;
}

interface Course {
  id: number;
  channel_id: number;
  title: string;
  is_active: boolean;
}

interface Channel {
  id: number;
  chat_id: number;
  title: string;
}

const TYPE_ICON: Record<string, string> = {
  video: '📹',
  document: '📄',
  photo: '🖼',
  audio: '🎵',
  voice: '🎤',
  text: '📝',
  poll: '📊',
};

const MEDIA_TYPES = ['video', 'document', 'photo', 'audio', 'voice'] as const;
const TYPE_LABEL_KEY: Record<string, TKey> = {
  video: 'material.typeVideo',
  document: 'material.typeDocument',
  photo: 'material.typePhoto',
  audio: 'material.typeAudio',
  voice: 'material.typeVoice',
  text: 'material.typeText',
};

function statusBadge(m: Material, t: TFunction): { variant: BadgeVariant; label: string } {
  if (m.is_cancelled) return { variant: 'muted', label: t('calendar.statusCancelled') };
  if (m.published_at) return { variant: 'success', label: t('calendar.statusPublished') };
  if (m.last_error) return { variant: 'destructive', label: t('calendar.statusError') };
  return { variant: 'warning', label: t('calendar.statusPending') };
}

export function CalendarPage() {
  const t = useT();
  const [creating, setCreating] = useState(false);
  const [reschedulingId, setReschedulingId] = useState<number | null>(null);
  const [newScheduledAt, setNewScheduledAt] = useState('');

  const qc = useQueryClient();
  const [monthStart] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const monthEnd = useMemo(() => {
    const d = new Date(monthStart);
    d.setMonth(d.getMonth() + 1);
    return d;
  }, [monthStart]);

  const list = useQuery({
    queryKey: ['materials', 'calendar', monthStart.toISOString()],
    queryFn: () =>
      apiFetch<{ items: Material[] }>('/api/admin/materials/calendar', {
        query: { from: monthStart.toISOString(), to: monthEnd.toISOString() },
      }),
  });

  const courses = useQuery({
    queryKey: ['courses', 'all'],
    queryFn: () => apiFetch<{ courses: Course[] }>('/api/admin/courses'),
  });

  const channels = useQuery({
    queryKey: ['channels'],
    queryFn: () => apiFetch<{ channels: Channel[] }>('/api/admin/channels'),
  });

  const channelById = useMemo(() => {
    const m = new Map<number, Channel>();
    channels.data?.channels.forEach((c) => m.set(c.id, c));
    return m;
  }, [channels.data]);

  const toastTop = useToast();

  const deleteMat = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/admin/materials/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toastTop.success(t('calendar.materialDeleted'));
      qc.invalidateQueries({ queryKey: ['materials'] });
    },
    onError: (err) => toastTop.error((err as ApiError).message ?? t('calendar.deleteFailed')),
  });

  const reschedule = useMutation({
    mutationFn: ({ id, when }: { id: number; when: string }) =>
      apiFetch(`/api/admin/materials/${id}/reschedule`, {
        method: 'PATCH',
        body: { scheduled_at: when },
      }),
    onSuccess: () => {
      setReschedulingId(null);
      toastTop.success(t('calendar.rescheduled'));
      qc.invalidateQueries({ queryKey: ['materials'] });
    },
    onError: (err) => toastTop.error((err as ApiError).message ?? t('calendar.rescheduleFailed')),
  });

  const byDay = useMemo(() => {
    if (!list.data) return new Map<string, Material[]>();
    const map = new Map<string, Material[]>();
    for (const m of list.data.items) {
      const day = new Date(m.scheduled_at).toISOString().slice(0, 10);
      const arr = map.get(day) ?? [];
      arr.push(m);
      map.set(day, arr);
    }
    return map;
  }, [list.data]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">
          {t('calendar.monthCaption', {
            month: monthStart.toLocaleString(intlLocale(), { month: 'long', year: 'numeric' }),
          })}
        </div>
        <Button onClick={() => setCreating(true)} disabled={!courses.data?.courses.length}>
          <Plus className="h-4 w-4" />
          {t('calendar.addMaterial')}
        </Button>
      </div>

      {list.isLoading ? (
        <PageLoader />
      ) : !list.data || list.data.items.length === 0 ? (
        <EmptyState
          icon={<CalendarIcon className="h-10 w-10" />}
          title={t('calendar.emptyTitle')}
          description={
            !courses.data?.courses.length
              ? t('calendar.emptyNoCourses')
              : t('calendar.emptyHint')
          }
        />
      ) : (
        <div className="space-y-3">
          {[...byDay.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([day, items]) => (
              <Card key={day}>
                <CardContent className="space-y-2 p-4">
                  <div className="text-sm font-semibold text-slate-700">
                    {new Date(day).toLocaleDateString(intlLocale(), {
                      weekday: 'short',
                      day: '2-digit',
                      month: 'long',
                    })}
                  </div>
                  <ul className="space-y-1">
                    {items.map((m) => {
                      const st = statusBadge(m, t);
                      const course = courses.data?.courses.find((c) => c.id === m.course_id);
                      const channel = course ? channelById.get(course.channel_id) : null;
                      return (
                        <li key={m.id} className="flex items-center gap-3 text-sm">
                          <span>{TYPE_ICON[m.type_code] ?? '•'}</span>
                          <span className="flex-1 truncate">
                            <span className="text-slate-500">[{m.course_title}]</span>{' '}
                            {channel && (
                              <span className="text-xs text-slate-400">
                                <Radio className="inline h-3 w-3" /> {channel.title}{' '}
                              </span>
                            )}
                            {m.title ?? t('calendar.untitled')}
                          </span>
                          <span className="text-xs text-slate-500">
                            {formatDateTime(m.scheduled_at)}
                          </span>
                          <Badge variant={st.variant}>{st.label}</Badge>
                          {!m.published_at && !m.is_cancelled && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setReschedulingId(m.id);
                                  setNewScheduledAt(toLocalDateTimeInput(m.scheduled_at));
                                }}
                                title={t('calendar.reschedule')}
                              >
                                <Clock className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  if (confirm(t('calendar.confirmCancel'))) deleteMat.mutate(m.id);
                                }}
                                title={t('calendar.cancelPublish')}
                              >
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            </>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </CardContent>
              </Card>
            ))}
        </div>
      )}

      <Dialog open={creating} onClose={() => setCreating(false)} title={t('calendar.newMaterial')} size="lg">
        <MaterialForm
          courses={courses.data?.courses ?? []}
          channelById={channelById}
          onCancel={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['materials'] });
          }}
        />
      </Dialog>

      <Dialog
        open={reschedulingId !== null}
        onClose={() => setReschedulingId(null)}
        title={t('calendar.rescheduleTitle')}
        description={t('calendar.rescheduleDescription')}
      >
        <div className="space-y-3">
          <Input
            type="datetime-local"
            value={newScheduledAt}
            onChange={(e) => setNewScheduledAt(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setReschedulingId(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              loading={reschedule.isPending}
              disabled={!newScheduledAt}
              onClick={() =>
                reschedulingId !== null &&
                reschedule.mutate({
                  id: reschedulingId,
                  when: new Date(newScheduledAt).toISOString(),
                })
              }
            >
              {t('calendar.reschedule')}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

function toLocalDateTimeInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface UploadResult {
  type_code: 'video' | 'document' | 'photo' | 'audio' | 'voice';
  file_id: string;
  file_unique_id: string;
  size_bytes: number;
  mime_type: string | null;
}

// Фактический лимит отдаёт сервер (50 MB облачный Bot API / 2000 MB локальный).
// Пока запрос не вернулся — консервативные 50 MB.
const DEFAULT_UPLOAD_BYTES = 50 * 1024 * 1024;

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes >= 1000 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function limitLabel(bytes: number): string {
  const mb = bytes / 1024 / 1024;
  return mb >= 1000 ? `${(mb / 1000).toFixed(0)} GB` : `${mb.toFixed(0)} MB`;
}

function MaterialForm({
  courses,
  channelById,
  onCancel,
  onCreated,
}: {
  courses: Course[];
  channelById: Map<number, Channel>;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const [courseId, setCourseId] = useState<number>(courses[0]?.id ?? 0);
  const [typeCode, setTypeCode] = useState<string>('video');
  const [title, setTitle] = useState('');
  const [caption, setCaption] = useState('');
  const [fileId, setFileId] = useState('');
  const [fileUniqueId, setFileUniqueId] = useState('');
  const [uploadedFilename, setUploadedFilename] = useState<string | null>(null);
  const [uploadedSize, setUploadedSize] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showManualFileId, setShowManualFileId] = useState(false);
  const [textBody, setTextBody] = useState('');
  const [scheduledAt, setScheduledAt] = useState(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 5);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [pinMessage, setPinMessage] = useState(false);
  const [disableNotification, setDisableNotification] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const toast = useToast();
  const t = useT();

  const uploadLimit = useQuery({
    queryKey: ['materials', 'upload-limit'],
    queryFn: () => apiFetch<{ max_bytes: number }>('/api/admin/materials/upload-limit'),
    staleTime: 10 * 60 * 1000,
  });
  const maxUploadBytes = uploadLimit.data?.max_bytes ?? DEFAULT_UPLOAD_BYTES;

  // При unmount — отменяем активную загрузку, иначе orphan file_id на бэке
  // (он уже отправлен в Telegram, но не привязан ни к одному материалу).
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const selectedCourse = courses.find((c) => c.id === courseId);
  const selectedChannel = selectedCourse ? channelById.get(selectedCourse.channel_id) : null;

  const isMedia = MEDIA_TYPES.includes(typeCode as (typeof MEDIA_TYPES)[number]);
  const isText = typeCode === 'text';

  const resetFile = () => {
    setFileId('');
    setFileUniqueId('');
    setUploadedFilename(null);
    setUploadedSize(null);
    setUploadError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);

    if (file.size > maxUploadBytes) {
      setUploadError(
        t('material.tooLarge', { size: humanSize(file.size), limit: limitLabel(maxUploadBytes) }),
      );
      e.target.value = '';
      return;
    }

    // Отменяем предыдущую загрузку, если пользователь поменял файл
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsUploading(true);
    setUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      const result = await apiUpload<UploadResult>('/api/admin/materials/upload', fd, {
        signal: controller.signal,
        onProgress: setUploadProgress,
      });
      // Если форму закрыли пока ждали — игнорируем результат
      if (controller.signal.aborted) return;
      setFileId(result.file_id);
      setFileUniqueId(result.file_unique_id);
      setUploadedFilename(file.name);
      setUploadedSize(result.size_bytes);
      if (MEDIA_TYPES.includes(result.type_code as (typeof MEDIA_TYPES)[number])) {
        setTypeCode(result.type_code);
      }
    } catch (err) {
      // AbortError — нормальное поведение при unmount
      if (err instanceof DOMException && err.name === 'AbortError') return;
      const msg = err instanceof Error ? err.message : t('material.uploadFailed');
      setUploadError(msg);
      toast.error(t('material.uploadFailedWith', { message: msg }));
      e.target.value = '';
    } finally {
      if (!controller.signal.aborted) setIsUploading(false);
    }
  };

  const create = useMutation({
    mutationFn: () =>
      apiFetch('/api/admin/materials', {
        method: 'POST',
        body: {
          course_id: courseId,
          type_code: typeCode,
          title: title || null,
          caption: caption || null,
          file_id: isMedia ? fileId : null,
          file_unique_id: isMedia ? fileUniqueId || null : null,
          text_body: isText ? textBody : null,
          scheduled_at: new Date(scheduledAt).toISOString(),
          pin_message: pinMessage,
          disable_notification: disableNotification,
        },
      }),
    onSuccess: () => {
      toast.success(t('material.scheduled'));
      onCreated();
    },
    onError: (err) => toast.error((err as ApiError).message ?? t('material.createFailed')),
  });

  const formValid =
    courseId && typeCode && scheduledAt && (isMedia ? fileId.length > 10 : textBody.trim().length > 0);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (formValid && !isUploading) create.mutate();
      }}
      className="space-y-3"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label>{t('material.course')}</Label>
          <select
            value={courseId}
            onChange={(e) => setCourseId(Number(e.target.value))}
            className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
          >
            {courses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title} {c.is_active ? '' : t('material.courseArchived')}
              </option>
            ))}
          </select>
          {selectedChannel && (
            <p className="mt-1 text-xs text-slate-500">
              <Radio className="inline h-3 w-3" /> {t('courses.channel')}:{' '}
              <b>{selectedChannel.title}</b>{' '}
              <span className="font-mono">({selectedChannel.chat_id})</span>
            </p>
          )}
        </div>
        <div>
          <Label>{t('material.type')}</Label>
          <select
            value={typeCode}
            onChange={(e) => setTypeCode(e.target.value)}
            className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
          >
            {Object.entries(TYPE_LABEL_KEY).map(([code, labelKey]) => (
              <option key={code} value={code}>
                {TYPE_ICON[code]} {t(labelKey)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <Label>{t('material.titleForAdmin')}</Label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="mt-1"
          placeholder={t('material.titlePlaceholder')}
        />
      </div>

      {isMedia && (
        <>
          <div>
            <Label>{t('material.file')}</Label>
            {fileId && uploadedFilename ? (
              <div className="mt-1 flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 p-3">
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <FileText className="h-4 w-4 text-emerald-600" />
                  <div>
                    <div className="font-medium">{uploadedFilename}</div>
                    <div className="text-xs text-slate-500">
                      {uploadedSize !== null && humanSize(uploadedSize)} ·{' '}
                      {t('material.uploadedToTelegram')}
                    </div>
                  </div>
                </div>
                <Button type="button" size="sm" variant="ghost" onClick={resetFile}>
                  <X className="h-4 w-4" />
                  {t('material.changeFile')}
                </Button>
              </div>
            ) : fileId && !uploadedFilename ? (
              <div className="mt-1 flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs text-slate-600">
                  {t('material.usingExistingFileId', { prefix: fileId.slice(0, 24) })}
                </div>
                <Button type="button" size="sm" variant="ghost" onClick={resetFile}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <label
                className={`mt-1 flex h-24 cursor-pointer items-center justify-center rounded-md border border-dashed ${
                  isUploading ? 'border-slate-300 bg-slate-50' : 'border-slate-300 bg-white hover:bg-slate-50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  disabled={isUploading}
                  onChange={handleFileChange}
                  accept="video/*,image/*,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/zip,application/x-zip-compressed,text/plain,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar"
                />
                {isUploading ? (
                  <div className="flex w-full max-w-xs flex-col items-center gap-2 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <Spinner className="h-5 w-5 text-slate-400" />
                      {/* После 100% сервер ещё отправляет файл в Telegram */}
                      {uploadProgress < 1
                        ? t('material.uploadingProgress', { percent: Math.floor(uploadProgress * 100) })
                        : t('material.uploading')}
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                      <div
                        className="h-full bg-slate-600 transition-[width]"
                        style={{ width: `${Math.floor(uploadProgress * 100)}%` }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1 text-sm text-slate-600">
                    <Upload className="h-5 w-5 text-slate-400" />
                    <span>{t('material.pickFile')}</span>
                    <span className="text-xs text-slate-500">
                      {t('material.fileHint', { limit: limitLabel(maxUploadBytes) })}
                    </span>
                  </div>
                )}
              </label>
            )}
            {uploadError && <p className="mt-1 text-sm text-red-600">{uploadError}</p>}

            <button
              type="button"
              onClick={() => setShowManualFileId((v) => !v)}
              className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
            >
              <ChevronDown
                className={`h-3 w-3 transition-transform ${showManualFileId ? 'rotate-180' : ''}`}
              />
              {t('material.manualFileId')}
            </button>
            {showManualFileId && (
              <div className="mt-2 space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3">
                <div>
                  <Label className="text-xs">file_id</Label>
                  <Textarea
                    value={fileId}
                    onChange={(e) => {
                      setFileId(e.target.value);
                      setUploadedFilename(null);
                      setUploadedSize(null);
                    }}
                    className="mt-1 font-mono text-xs"
                    rows={2}
                    placeholder="BAACAgIAAxkBAAIB..."
                  />
                </div>
                <div>
                  <Label className="text-xs">{t('material.fileUniqueIdOptional')}</Label>
                  <Input
                    value={fileUniqueId}
                    onChange={(e) => setFileUniqueId(e.target.value)}
                    className="mt-1 font-mono text-xs"
                    placeholder="AgADRgUAAlGcCFI"
                  />
                </div>
              </div>
            )}
          </div>
          <div>
            <Label>{t('material.caption')}</Label>
            <Textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="mt-1"
              rows={2}
              placeholder={t('material.captionPlaceholder')}
            />
          </div>
        </>
      )}

      {isText && (
        <div>
          <Label>{t('material.textBody')}</Label>
          <Textarea
            value={textBody}
            onChange={(e) => setTextBody(e.target.value)}
            className="mt-1"
            rows={5}
            placeholder={t('material.textBodyPlaceholder')}
          />
        </div>
      )}

      <div>
        <Label>{t('material.scheduledAt')}</Label>
        <Input
          type="datetime-local"
          value={scheduledAt}
          onChange={(e) => setScheduledAt(e.target.value)}
          className="mt-1"
        />
        <p className="mt-1 text-xs text-slate-500">{t('material.tzHint')}</p>
      </div>

      <div className="flex gap-4 text-sm">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={pinMessage}
            onChange={(e) => setPinMessage(e.target.checked)}
          />
          {t('material.pin')}
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={disableNotification}
            onChange={(e) => setDisableNotification(e.target.checked)}
          />
          {t('material.silent')}
        </label>
      </div>

      {create.isError && (
        <p className="text-sm text-red-600">{(create.error as { message?: string }).message}</p>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" loading={create.isPending} disabled={!formValid || isUploading}>
          {t('common.create')}
        </Button>
      </div>
    </form>
  );
}
